import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as registeredNodeSelectors from 'data/registeredNode/registeredNode.selectors';
import * as registeredNodeActions from 'data/registeredNode/registeredNode.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as locationSelectors from 'data/location/location.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { RegisteredNode } from 'data/registeredNode/registeredNode.types';
import RegisteredNodeForm from 'routes/RegisteredNodes/components/RegisteredNodeForm';
import * as types from 'data/registeredNode/registeredNode.types';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as nodesActions from 'data/nodes/nodes.actions';
import * as locationActions from 'data/location/location.actions';

import { registeredNodeTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptFormData,
  adaptRegisteredNodeFormData,
  adaptRegisteredNodeTemplate,
  adaptLocationField,
  adaptTemplateBasedOnAppSettings,
} from '../../utils/registeredNodeTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/registeredNodeTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.RegisteredNode
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  registeredNodeTemplateDef: adaptTemplateVisibilityMode(registeredNodeTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  selectedRegisteredNode: registeredNodeSelectors
    .getSelectedRegisteredNode(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedRegisteredNode: registeredNodeSelectors
    .getSelectedRegisteredNode(state)[0],
  registeredNode: registeredNodeSelectors.getRegisteredNode(state),
  nodes: nodesSelectors.getNodes(state),
  countries: locationSelectors.getCountryList(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
});

const mapDispatch = {
  goToRegisteredNodeList: registeredNodeActions.goToRegisteredNodeList,
  goToRegisteredNodeEdit: registeredNodeActions.goToRegisteredNodeEdit,
  getRegisteredNode: registeredNodeActions.getRegisteredNode,
  getNodes: nodesActions.getNodes,
  fetchLocationList: locationActions.fetchLocationList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RegisteredNodeView = ({
  history,
  selectedRegisteredNode,
  originalSelectedRegisteredNode,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  registeredNodeTemplateDef,
  registeredNode,
  isLocationRequired,
  goToRegisteredNodeList,
  getRegisteredNode,
  goToRegisteredNodeEdit,
  getNodes,
  fetchLocationList,
  nodes,
  countries,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<RegisteredNode>>({});
  const [template, setTemplate] = useState(registeredNodeTemplateDef);
  const { t } = useTranslation();

  const id = selectedRegisteredNode?.id;

  useEffect(() => {
    if (id) {
      getRegisteredNode(deliveryFunctionId, id);
    }
    getNodes(deliveryFunctionId);
    fetchLocationList();
  }, [deliveryFunctionId, id, getRegisteredNode, getNodes, fetchLocationList]);

  useEffect(() => {
    if (registeredNode != null && template != null) {
      let clonedRegisteredNode = cloneDeep(registeredNode);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedRegisteredNode = adaptRegisteredNodeFormData(clonedRegisteredNode);

      let adaptedTemplate = adaptTemplateOptions(registeredNodeTemplateDef, countries, clonedRegisteredNode, nodes);

      if (adaptedTemplate && clonedRegisteredNode.stateName) {
        adaptedTemplate = adaptLocationField(adaptedTemplate, [clonedRegisteredNode.stateName], 'stateName');
      }

      if (adaptedTemplate && clonedRegisteredNode.city) {
        adaptedTemplate = adaptLocationField(adaptedTemplate, [clonedRegisteredNode.city], 'city');
      }

      adaptedTemplate = adaptTemplateBasedOnAppSettings(
        adaptedTemplate,
        isLocationRequired,
      )

      setTemplate(adaptedTemplate);
      setFormData(clonedRegisteredNode);
    }
  }, [registeredNode, id]);

  const handleEdit = () => goToRegisteredNodeEdit(history);

  const handleCancel = () => goToRegisteredNodeList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as RegisteredNode);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptRegisteredNodeTemplate(clonedTemplate);

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <RegisteredNodeForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (registeredNode == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="RegisteredNodeView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('registeredNode.view.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(RegisteredNodeView));
